import type { HTTPMethod } from 'h3'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'

import type { SessionType } from '~/types'

export type Options = NitroFetchOptions<NitroFetchRequest>

export type WithOptions<T = unknown> = T & {
  options?: Options
}

interface ApiResponse<T> {
  data: T
}

interface ApiResponseWithMeta<T> extends ApiResponse<T> {
  meta: {
    pagination: {
      items: number
      total: number
      has_pages: boolean
      is_first: boolean
      pointer: {
        previous: number | null
        current: number
        next: number | null
      }
    }
  }
}

export const setDefaultApiResponseWithMeta = (): ApiResponseWithMeta<[]> => ({
  data: [],
  meta: {
    pagination: {
      items: 0,
      total: 0,
      has_pages: false,
      is_first: true,
      pointer: {
        previous: null,
        current: 1,
        next: null,
      },
    },
  },
})

type Endpoint =
  | 'languages'
  | 'sessions/authenticate'
  | 'sessions/current'
  | 'sessions/current/complete'
  | 'sessions/current/contract/finalize'
  | 'sessions/current/refresh-participant-token'
  | 'template-collections'
  | 'templates'
  | `templates/${string}`

export const useInternalApi = () => {
  interface ReqParams {
    url: string
    accessType?: SessionType
    options?: NitroFetchOptions<NitroFetchRequest>
  }

  const getUrl = (sessionType: SessionType, endpoint: Endpoint) =>
    `/api/internal/${sessionType}/${endpoint}`

  const request = (httpMethod: HTTPMethod) =>
    async <ResponseT> ({
      url,
      accessType,
      options = {},
    }: ReqParams) => {
      let accessToken = ''
      if (accessType) {
        const { authData } = useSessionStorageAuth(accessType)
        if (authData.value?.access_token)
          accessToken = authData.value.access_token
      }
      return $fetch<ResponseT>(url, {
        method: httpMethod,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...options.headers,
        },
        ...options,
      })
    }

  return {
    getUrl,
    get: <T>(params: ReqParams) => request('GET')<ApiResponse<T>>(params),
    getMany: <T>(params: ReqParams) => request('GET')<ApiResponseWithMeta<T>>(params),
    post: <T>(params: ReqParams) => request('POST')<ApiResponse<T>>(params),
    put: <T>(params: ReqParams) => request('PUT')<ApiResponse<T>>(params),
    drop: <T>(params: ReqParams) => request('DELETE')<ApiResponse<T>>(params),
  }
}
